<template>
  <OperationFormLayoutSkeleton
    v-if="loading"
    isConfigurationForm
  />
  <form @submit.prevent="save">
    <OperationFormLayout
      v-if="!loading"
      :formDisabled="disable"
      :formSaving="saving"
      isConfigurationForm
      operationType="organisation"
      @cancel="back"
    >
      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('organisations.details_description')"
        :sectionTitle="$t('organisations.details_title')"
      >
        <FormRow
          :errors="errors && errors.organisation.name"
          :label="$t('organisations.organisation_name_label')"
          labelFor="organisation_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ 'input': true, 'input--error': errors && errors.organisation.name }">
              <input
                id="organisation_name"
                v-model="organisation.name"
                type="text"
              >
            </div>
          </div>
        </FormRow>
      </OperationFormSection>

      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('organisations.logo_description')"
        :sectionTitle="$t('organisations.logo_title')"
      >
        <div class="form-layout__content organisation-settings__logo-section">
          <div
            class="organisation-settings__logo-wrapper organisation-logo"
            :data-content="organisation.name ? organisation.name[0].toUpperCase() : ''"
          >
            <img
              v-if="organisation.logo"
              class="organisation-settings__logo"
              :src="organisation.logo[100]"
            >
          </div>
          <div class="organisation-settings__logo-actions">
            <div
              v-if="!organisation.logo"
              class="organisation-settings__logo-input-wrapper"
            >
              <label
                class="organisation-settings__logo-input-button button"
                for="organisation_logo"
              >
                {{ $t('organisations.upload_image') }}
              </label>
              <input
                id="organisation_logo"
                class="organisation-settings__logo-input"
                type="file"
                @change="selectLogo"
              >
            </div>
            <button
              v-if="organisation.logo"
              class="button"
              type="button"
              @click="removeLogo"
            >
              {{ $t('organisations.remove_image') }}
            </button>
          </div>
        </div>
      </OperationFormSection>
    </OperationFormLayout>
  </form>
</template>

<script setup>
import { onMounted, reactive, ref, watch, nextTick } from 'vue';

// Utils
import { useRoute, useRouter } from 'vue-router';
import useUploader from '@/vendor/use_uploader';
import useAjax from '@/functions/use_ajax';
import readFile from '@/functions/read_file';
import isDesktop from '@/functions/responsive';

// Components
import FormRow from '@/components/form/row.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';

const route = useRoute();
const router = useRouter();
const uploader = useUploader();
const ajax = useAjax();

const loading = ref(true);
const organisation = reactive(createNewOrganisation());
const initialOrganisation = ref(null);
const errors = ref(null);
const saving = ref(false);
const disable = ref(true);

let newLogoUpload = null;

/**
 * Form validation, check if the form was updated,
 * if form is updated enable the save button
 */
watch(() => organisation, (newValue) => {
    redirect(newValue);
    const formFields = [
      'name',
      'logo',
      'newLogo'
    ];

    const changedFields = formFields.filter((field) => {
      // Check if a logo was set (if it was then a logo object will exist)
      if (newValue[field] && typeof newValue[field] === 'object' && !initialOrganisation.value[field]) {
        return newValue[field]?.id !== initialOrganisation.value['logo'];
      }
      // Compare logo objects
      if (newValue[field] && typeof newValue[field] === 'object') {
        return (JSON.stringify(newValue[field]) !== JSON.stringify(initialOrganisation.value[field]));
      }
      // Compare other string instances
      if (['name'].includes(field)) {
        return newValue[field].toString() !== initialOrganisation.value[field].toString();
      }
    });
    disable.value = changedFields.length <= 0;
  },
  { deep: true }
);

onMounted(async () => {
  loading.value = true;
  if (route.params.organisationId) {
    let { ok, data } = await ajax(`/organisations/${route.params.organisationId}`);

    if (ok) {
      Object.assign(organisation, data);
      initialOrganisation.value = data;
    }
  }
  loading.value = false;
});

function createNewOrganisation() {
  return {
    name: '',
    logo: null,
    newLogo: null,
  };
}

function redirect(data) {
  !data?.policy?.edit && back();
}

/**
 * @param {{ target: { files: File[] }}} param
 */
async function selectLogo({ target: { files: [file] } }) {
  if (!file) return;

  let url = await readFile(file);

  organisation.logo = { 32: url, 72: url, 100: url };
  newLogoUpload = uploader.create(file);
}

async function removeLogo() {
  newLogoUpload = null;
  organisation.logo = null;
  await nextTick(() => {
    disable.value = false;
  });
}

async function formatOrganisationForPost() {
  let formattedOrganisation = {
    name: organisation.name,
  };

  if (newLogoUpload) {
    let blob = await uploader.upload(newLogoUpload);
    formattedOrganisation.logo = blob.signed_id;
  } else if (!organisation.logo && route.params.organisationId) {
    formattedOrganisation.remove_logo = true;
  }

  return { organisation: formattedOrganisation };
}

async function save() {
  saving.value = true;
  errors.value = null;

  let url = route.params.organisationId ? `/organisations/${route.params.organisationId}` : '/organisations';
  let method = route.params.organisationId ? 'PATCH' : 'POST';
  let body = await formatOrganisationForPost();

  let { ok, data } = await ajax(url, { method, body });

  if (ok) {
    router.replace({
      name: isDesktop() ? 'operations' : 'organisationConfiguration',
      params: { organisationId: data.id }
    });
  } else {
    errors.value = data;
  }

  saving.value = false;
}

function back() {
  router.replace({
    name: isDesktop() ? 'operations' : 'organisationConfiguration',
    params: { organisationId: route.params.organisationId },
  });
}
</script>
